import {
	type FunctionComponent,
	type ReactElement,
	type PropsWithChildren,
	cloneElement,
	useEffect,
	useState,
	useRef,
	useContext
} from 'react';
import { useInView } from 'react-intersection-observer';
import { type ZIndices } from '../../../constants/general';
import { ScrollLockContext } from '../../../contexts/scroll-lock/scroll-lock.context';
import { handleKeys, listenToKeys } from '../../../helpers/keyboard/keyboard.helper';
import { type ButtonStyle, StyledButton, ClickableElement } from '../../buttons';
import XCloseIcon from '../../svgs/untitled-ui/x-close.svg';

import { Overlay } from '../overlay/overlay.component';
import { Portal } from '../portal/portal.component';
import {
	closeButtonStyle,
	modalHeightStretch,
	modalMaxWidth,
	modalMaxWidthLarge,
	modalMaxWidthNormal,
	modalMaxWidthSmall
} from './modal.css';

const BASE_WRAPPER_CLASSES = 'flex justify-center items-start h-100';

const MODAL_SIZE_MAP = {
	small: `w-100 ${modalMaxWidthSmall}`,
	normal: `w-100 ${modalMaxWidthNormal}`,
	large: `w-100 ${modalMaxWidthLarge}`,
	stretch: `w-100 w-90-ns ${modalHeightStretch} ${modalMaxWidth}`,
	// sized based on its content. Originally intended for video players
	fit: `dib ${modalMaxWidth}`
};

export type ModalSizes = keyof typeof MODAL_SIZE_MAP;

export type ModalAction = {
	title: string;
	automationHook?: string;
	onClick: () => void;
	disabled?: boolean;
	isBusy?: boolean;
	busyText?: string;
	buttonStyle?: Extract<ButtonStyle, 'PRIMARY' | 'SECONDARY' | 'INTERNAL' | 'DANGER'>;
};

export type ModalPositioningProps = {
	shouldScroll: boolean;
	zIndex?: ZIndices;
};

const ModalPositioning: FunctionComponent<PropsWithChildren<ModalPositioningProps>> = ({ children, shouldScroll, zIndex }) => {
	const wrapperClasses = shouldScroll ? `${BASE_WRAPPER_CLASSES} overflow-y-auto` : BASE_WRAPPER_CLASSES;
	return (
		<div className={`fixed z-${zIndex} left-0 top-0 w-100 h-100 w-100`}>
			<div className={wrapperClasses}>{children}</div>
		</div>
	);
};

/**
 * Modal must be passed either a title OR an ariaLabel OR both
 */
export type ModalProps = {
	primaryAction?: ModalAction;
	secondaryActions?: ModalAction[];
	closeAction?: () => void;
	triggerElement?: ReactElement | JSX.Element;
	size?: ModalSizes;
	onOpen?: Function;
	noCloseButton?: boolean;
	leftAlignTitle?: boolean;
	className?: string;
	disableClose?: boolean;
	// If true (default), add padding on the modal body. If false, do not add padding.
	padBody?: boolean;
	isInternal?: boolean;
	zIndex?: ZIndices;
	// whether or not we restore focus after the modal is closed
	restoreFocus?: boolean;
	customCloseButton?: React.ReactElement;
} & (
	| {
			title: string | JSX.Element;
			ariaLabel?: string;
	  }
	| {
			title?: string | JSX.Element;
			ariaLabel: string;
	  }
);

export type ModalTriggerProps = {
	triggerElement: ReactElement | JSX.Element;
	openModal: () => void;
	ariaLabel: string;
	testId?: string;
	className?: string;
};

export const ModalTrigger: FunctionComponent<ModalTriggerProps> = ({ triggerElement, openModal, ariaLabel, testId }) => {
	const hasClickableTrigger = Boolean(
		triggerElement?.type === 'button' ||
			(typeof triggerElement?.type === 'function' && triggerElement?.type?.name === 'StyledButton') ||
			triggerElement?.props?.buttonStyle
	);
	return hasClickableTrigger && triggerElement ? (
		cloneElement(triggerElement, { onClick: openModal, 'aria-label': ariaLabel, ...triggerElement.props })
	) : (
		<ClickableElement
			ariaLabel={ariaLabel}
			className="w-inherit"
			onClick={openModal}
			onKeyDown={handleKeys(['Enter', ' '], openModal)}
			data-testid={testId}
			tabIndex={0}>
			{triggerElement}
		</ClickableElement>
	);
};

export const Modal: FunctionComponent<PropsWithChildren<ModalProps>> = ({
	triggerElement,
	title,
	ariaLabel,
	primaryAction,
	secondaryActions = [],
	children,
	closeAction,
	size = 'normal',
	onOpen,
	noCloseButton = false,
	leftAlignTitle = false,
	className = '',
	disableClose = false,
	padBody = true,
	zIndex = '5',
	isInternal = false,
	restoreFocus = true,
	customCloseButton
}) => {
	const { setIsScrollLocked } = useContext(ScrollLockContext);
	const [showModal, setShowModal] = useState(triggerElement === undefined);
	const [shouldScroll, setShouldScroll] = useState(false);
	const modalRef = useRef<HTMLDivElement>(null);
	const titleValue = typeof title === 'string' ? title : undefined;
	const modalName = titleValue || ariaLabel || 'unknown';
	const portalChildId = `modal-portal-${modalName.replace(/\s+/g, '-').toLowerCase()}`;

	function close() {
		if (!disableClose) {
			setShowModal(false);
		}
		if (closeAction) {
			closeAction();
		}
	}

	const openModal = () => {
		setShowModal(true);
	};

	/**
	 * Do not mount children until modal is mounted and available in the DOM. (Portal component will
	 * have mounted before modal component mounts, so this should solve race condition of the embedded
	 * video component mounting before the modal/portal is available in the DOM.)
	 */
	const [mountChildren, setMountChildren] = useState(false);
	useEffect(() => {
		setMountChildren(true);
	}, []);

	/**
	 * Set up the escape key to close the modal.
	 */
	const escapeHandler = listenToKeys(['Escape'], close);
	useEffect(() => {
		const currentRef = modalRef.current;
		if (showModal) {
			currentRef?.addEventListener('keydown', escapeHandler);
		}
		return () => {
			currentRef?.removeEventListener('keydown', escapeHandler);
		};
	}, [showModal]);

	/**
	 * When modal is open we append a overflow-hidden class to the document
	 * body to prevent scrolling.  We also remove the overflow-hidden class when
	 * the modal is unmounted
	 */
	useEffect(() => {
		if (showModal) {
			setIsScrollLocked(true);
			if (onOpen) {
				onOpen();
			}
		} else {
			setIsScrollLocked(false);
		}
		return () => setIsScrollLocked(false);
	}, [showModal, onOpen, setIsScrollLocked]);

	// if the modal is taller that the window then allow it's container to scroll
	const { ref: modalContentRef, entry } = useInView({ threshold: 1 });
	useEffect(() => {
		if (Number(entry?.intersectionRatio) < 1) {
			setShouldScroll(true);
		}
	}, [entry, entry?.intersectionRatio]);

	const closeButton = !noCloseButton ? (
		customCloseButton ? (
			cloneElement(customCloseButton, { onClick: close, onKeyDown: handleKeys(['Enter', ' '], close), tabIndex: 0 })
		) : (
			<ClickableElement
				ariaLabel="close modal"
				className={`absolute ${closeButtonStyle} pa1 w2 h2 pointer`}
				onClick={close}
				onKeyDown={handleKeys(['Enter', ' '], close)}
				tabIndex={0}>
				<XCloseIcon className="f4 pa1 theme-grey-darker hover-theme-black" data-testid="XCloseIcon" />
			</ClickableElement>
		)
	) : null;

	return (
		<>
			{showModal && (
				<Portal portalRootId="modal-root" portalChildId={portalChildId} restoreFocus={restoreFocus}>
					<ModalPositioning shouldScroll={shouldScroll} zIndex={zIndex}>
						<div
							data-testid="modal"
							className={`${MODAL_SIZE_MAP[size]} z-5 tc pa3 pa0-ns mt6-ns ${className}`}
							ref={modalRef}
							role="dialog"
							aria-modal="true"
							aria-label={titleValue || ariaLabel}>
							<div className="overflow-hidden br3 lh-copy shadow-3 mb5 h-100 relative">
								{closeButton}
								{title && (
									<div
										className={`pv3 ph5 br3 br--top f4 f3-ns font-medium lh-title tc ${
											isInternal ? 'bg-theme-internal-lighter' : 'bg-theme-grey-lighter'
										}`}
										data-testid="ModalHeader">
										<div className={`tc2-title ${leftAlignTitle ? 'tl' : ''}`}>{title}</div>
									</div>
								)}
								<div
									ref={modalContentRef}
									className={`lh-copy bg-theme-white h-100 ${(title || primaryAction) && padBody ? 'pa4' : ''}`}
									data-testid="ModalContent">
									{mountChildren && children}
								</div>
								{primaryAction && (
									<div
										className={`ph4 pb4 flex flex-column flex-row-ns flex-wrap justify-end bg-theme-white`}
										data-testid="ModalFooter">
										{secondaryActions.map((action, index) => (
											<div key={index} className="ml2-ns mb2 mb0-ns w-100 w-auto-ns">
												<StyledButton
													onClick={action.onClick}
													automationHook={action.automationHook}
													disabled={action.disabled}
													testId="secondary-action"
													buttonStyle="SECONDARY">
													{action.title}
												</StyledButton>
											</div>
										))}
										<div className="ml2-ns w-100 w-auto-ns">
											<StyledButton
												onClick={primaryAction.onClick}
												automationHook={primaryAction.automationHook}
												disabled={primaryAction.disabled}
												isBusy={primaryAction.isBusy}
												busyText={primaryAction.busyText}
												testId="primary-action"
												buttonStyle={primaryAction.buttonStyle}>
												{primaryAction.title}
											</StyledButton>
										</div>
									</div>
								)}
							</div>
						</div>
						<Overlay zIndex={'4'} style="dark" onClick={close}></Overlay>
					</ModalPositioning>
				</Portal>
			)}

			{triggerElement && <ModalTrigger triggerElement={triggerElement} openModal={openModal} ariaLabel={modalName} testId="toggle" />}
		</>
	);
};
